import React from 'react';
import Box from '@mui/material/Box';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import COLORS from '../../../constants/colors';
import { SectionCard } from '../../../components/Section';
import { PlanRulesDraft, PlanRulesPlan } from '../../../types/planRules';
import { isFreePlan, planLabel } from './premium.helpers';
import { LimitControl } from './LimitControl';

interface PlanTotalsProps {
  draft: PlanRulesDraft;
  onPlanChange: (planId: number, patch: Partial<PlanRulesPlan>) => void;
}

export function FieldBlock({ label, help, children }: { label: string; help?: string; children: React.ReactNode }) {
  return (
    <Box sx={{ mb: '20px' }}>
      <Typography sx={{ fontWeight: 600, mb: '6px' }}>{label}</Typography>
      {children}
      {help && <Typography sx={{ fontSize: '0.8125rem', color: COLORS.LIGHT_GRAY, mt: '6px' }}>{help}</Typography>}
    </Box>
  );
}

/** Plan totals: limits across all products, one card per plan. */
export function PlanTotals({ draft, onPlanChange }: PlanTotalsProps) {
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '16px' }}>
      {draft.plans.map((plan) => (
        <SectionCard
          key={plan.id}
          title={planLabel(plan)}
          description={
            isFreePlan(plan)
              ? 'Every user without an active premium subscription.'
              : 'Paid users. Name and price are managed in the App Store / Google Play.'
          }
        >
          <FieldBlock
            label="Portfolios a user can open in total"
            help="Across all products. When reached, the app shows the upgrade screen. Each product group can have its own, lower limit."
          >
            <LimitControl
              ariaLabel={`${plan.name}: portfolios`}
              value={plan.portfolioLimit}
              onChange={(value) => onPlanChange(plan.id, { portfolioLimit: value })}
            />
          </FieldBlock>

          <FieldBlock
            label="Saved recommendations"
            help="Example: limit 1 portfolio → after opening 1 portfolio, the user can't save a new recommendation."
          >
            <LimitControl
              ariaLabel={`${plan.name}: saved recommendations`}
              value={plan.recommendationLimit}
              onChange={(value) => onPlanChange(plan.id, { recommendationLimit: value })}
            />
            <FormControlLabel
              sx={{ mt: '8px', display: 'flex' }}
              control={
                <Switch
                  checked={plan.recommendationCappedByPortfolios}
                  onChange={(event) =>
                    onPlanChange(plan.id, { recommendationCappedByPortfolios: event.target.checked })
                  }
                />
              }
              label="Count saved recommendations as portfolio slots"
            />
          </FieldBlock>

          <FieldBlock label="Management fee">
            <FormControlLabel
              control={
                <Switch
                  checked={plan.managementFee}
                  onChange={(event) => onPlanChange(plan.id, { managementFee: event.target.checked })}
                />
              }
              label={plan.managementFee ? 'Charge the management fee' : "Don't charge the management fee"}
            />
          </FieldBlock>
        </SectionCard>
      ))}
    </Box>
  );
}
